// components/tables/analystTableSlice.ts
// Pure helper that turns an Analyst doc + A1 range into a formatted table slice
// (header + rows + per-column alignment). Shared by the live Master table card and
// by ANALYST_SHOW_IN_CHAT, so the snapshot embedded in the chat message renders
// identically to the live card.
import { computeWorkbook } from '@/components/asset-management/workbookEngine';
import { parseA1Range } from '@/components/tables/cellRef';

// A table cell is either plain text (Analyst) or a text + optional logo/avatar image
// (Source brand/contact cells). cellText() flattens either to a string for copy /
// download / alignment.
export type SliceCell = string | { text: string; logoUrl?: string };
export const cellText = (c: SliceCell): string => (typeof c === 'string' ? c : c.text);

export interface TableSlice {
  header: string[];
  rows: SliceCell[][];
  colAlign: ('left' | 'right')[];
  /** Per-column wrap: true = long-text column (capped width + line clamp); false/absent
   *  = keep on one line (nowrap). Absent means all columns are nowrap. */
  colWrap?: boolean[];
  sheetName: string;
}

interface SliceDoc {
  sheets: ReadonlyArray<{
    name: string;
    cells: Record<string, unknown>;
    styles?: Record<string, { numFmt?: string } | undefined>;
  }>;
}

type SsfFormatter = { format: (code: string, val: number) => string } | null | undefined;

const numDefault = (v: number): string =>
  Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { maximumFractionDigits: 2 });

// Right-align cells that read as a number (€1,234 / 8.3% / 1.5x / (123) / -45).
export const isNumericSliceText = (t: string): boolean =>
  /^[(+\-]?\s*[€$£]?\s*\d[\d.,\s]*\)?\s*[%x×]?$/.test(t.trim());

export function computeTableSlice(
  doc: SliceDoc,
  sheetName: string | undefined,
  range: string | undefined,
  ssf: SsfFormatter
): TableSlice | null {
  const sh =
    (sheetName ? doc.sheets.find((s) => s.name.toLowerCase() === sheetName.toLowerCase()) : null) ?? doc.sheets[0];
  if (!sh) return null;

  const rng = range ? parseA1Range(range) : null;
  let r0 = 0;
  let c0 = 0;
  let r1 = 0;
  let c1 = 0;
  if (rng) {
    r0 = rng.r0;
    c0 = rng.c0;
    r1 = rng.r1;
    c1 = rng.c1;
  } else {
    for (const key of Object.keys(sh.cells)) {
      const parts = key.split(',');
      const rr = Number(parts[0]);
      const cc = Number(parts[1]);
      if (Number.isFinite(rr) && rr > r1) r1 = rr;
      if (Number.isFinite(cc) && cc > c1) c1 = cc;
    }
  }

  let wb: Record<string, Record<string, unknown>> = {};
  try {
    wb = computeWorkbook(
      doc.sheets.map((s) => ({ name: s.name, cells: s.cells })) as unknown as Parameters<typeof computeWorkbook>[0]
    ) as unknown as Record<string, Record<string, unknown>>;
  } catch {
    /* best-effort: fall back to raw cell text */
  }
  const computed = wb[sh.name.toLowerCase()] ?? {};
  const styles = sh.styles ?? {};
  const fmt = (r: number, c: number): string => {
    const key = `${r},${c}`;
    const cv = computed[key];
    const raw = sh.cells[key];
    const v = cv !== undefined && cv !== null && cv !== '' ? cv : raw;
    if (v === undefined || v === null || v === '') return '';
    if (typeof v === 'number') {
      const code = styles[key]?.numFmt;
      if (code && ssf) {
        try {
          return ssf.format(code, v);
        } catch {
          return numDefault(v);
        }
      }
      return numDefault(v);
    }
    return String(v);
  };

  const header: string[] = [];
  for (let c = c0; c <= c1; c += 1) header.push(fmt(r0, c));
  const rows: string[][] = [];
  for (let r = r0 + 1; r <= r1; r += 1) {
    const row: string[] = [];
    for (let c = c0; c <= c1; c += 1) row.push(fmt(r, c));
    if (row.some((x) => x !== '')) rows.push(row);
  }
  // Per-column alignment: a column whose body cells are mostly numeric is right-aligned.
  const colAlign: ('left' | 'right')[] = header.map((_, ci) => {
    const vals = rows.map((r) => r[ci] ?? '').filter((x) => x !== '');
    const numeric = vals.filter((x) => isNumericSliceText(x)).length;
    return vals.length > 0 && numeric >= vals.length / 2 ? 'right' : 'left';
  });

  return { header, rows, colAlign, sheetName: sh.name };
}

// Load the Excel number-format renderer (SSF) once, shared. Returned formatter is
// null if xlsx fails to load (falls back to default number formatting).
let ssfCache: { format: (code: string, val: number) => string } | null = null;
let ssfLoading: Promise<{ format: (code: string, val: number) => string } | null> | null = null;
export const loadSsf = (): Promise<{ format: (code: string, val: number) => string } | null> => {
  if (ssfCache) return Promise.resolve(ssfCache);
  if (!ssfLoading) {
    ssfLoading = import('xlsx')
      .then((m) => {
        ssfCache = (m as unknown as { SSF: { format: (code: string, val: number) => string } }).SSF;
        return ssfCache;
      })
      .catch(() => null);
  }
  return ssfLoading;
};
export const getSsf = (): { format: (code: string, val: number) => string } | null => ssfCache;
